import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, AfterViewInit, SimpleChanges, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GoogleMapsModule, MapInfoWindow, GoogleMap, MapMarker } from '@angular/google-maps';
import { FlightPathData } from '../../../core/models/spray-record.model';
import { FlightMapService } from '../../../core/services/flight-map.service';

interface Coordinate {
  lat: number;
  lng: number;
}

/** Cached path data for rendering */
interface CachedPathData {
  path: FlightPathData;
  polylinePath: google.maps.LatLngLiteral[];
  polylineOptions: google.maps.PolylineOptions;
  startPosition: google.maps.LatLngLiteral | null;
  endPosition: google.maps.LatLngLiteral | null;
  startMarkerOptions: google.maps.MarkerOptions;
  endMarkerOptions: google.maps.MarkerOptions;
}

@Component({
  selector: 'app-flight-map',
  standalone: true,
  imports: [CommonModule, GoogleMapsModule],
  templateUrl: './flight-map.component.html',
  host: { class: 'd-block w-100 h-100' },
  styleUrls: ['./flight-map.component.scss']
  // Note: Removed OnPush - Google Maps requires default change detection for controls to work
})
export class FlightMapComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @ViewChild(GoogleMap) googleMap!: GoogleMap;
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;

  private flightMapService = inject(FlightMapService);

  /** Single flight path to display */
  @Input() flightPath: FlightPathData | null = null;

  /** Multiple flight paths (for multi-drone view) */
  @Input() flightPaths: FlightPathData[] = [];

  /** Height of the map container */
  @Input() height = '400px';

  /** Whether to show the upload overlay */
  @Input() showUploadOverlay = false;

  /** Whether the map is in view-only mode (no interactions) */
  @Input() viewOnly = false;

  /** Field center coordinates (for initial map position) */
  @Input() fieldCenter: Coordinate | null = null;

  /** Emits when a file is uploaded */
  @Output() fileUploaded = new EventEmitter<FlightPathData>();

  /** Emits when upload is cancelled */
  @Output() uploadCancelled = new EventEmitter<void>();

  // Map configuration - simplified to match CropFlight's working setup
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'satellite',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 20,
    minZoom: 2,
    streetViewControl: false
  };

  zoom = 15;
  // Default center: Agricultural area near Waco, TX
  center: google.maps.LatLngLiteral = { lat: 31.5493, lng: -97.1467 };

  // Base polyline options
  private basePolylineOptions: google.maps.PolylineOptions = {
    strokeColor: '#00AAFF',
    strokeOpacity: 0.9,
    strokeWeight: 3
  };

  // Colors for multiple flight paths
  pathColors = [
    '#00AAFF', // Blue
    '#FF6B35', // Orange
    '#7CB518', // Green
    '#9B5DE5', // Purple
    '#F15BB5', // Pink
    '#00BBF9'  // Cyan
  ];

  // CACHED data - computed once on input changes, not on every render
  cachedPaths: CachedPathData[] = [];

  // Info window content
  selectedPath: FlightPathData | null = null;

  // File upload state
  isDragging = false;
  isProcessing = false;
  uploadError: string | null = null;

  ngOnInit(): void {
    this.rebuildCache();
  }

  ngAfterViewInit(): void {
    // Don't call fitBounds here - wait for tilesloaded
  }

  private hasInitializedView = false;

  /**
   * Called when the Google Map is initialized
   */
  onMapReady(): void {
    // Trigger resize after a short delay to ensure container is properly sized
    setTimeout(() => {
      const map = this.googleMap?.googleMap;
      if (map) {
        google.maps.event.trigger(map, 'resize');
        // Auto-fit bounds on initial load only
        if (!this.hasInitializedView && this.cachedPaths.length > 0) {
          this.hasInitializedView = true;
          this.fitBounds();
        }
      }
    }, 100);
  }

  /**
   * Called when map tiles are loaded
   */
  onTilesLoaded(): void {
    // Map tiles loaded
  }

  ngOnDestroy(): void {
    // Nothing to clean up
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['flightPath'] || changes['flightPaths'] || changes['fieldCenter']) {
      this.rebuildCache();
    }
  }

  /**
   * Rebuild all cached data when inputs change
   */
  private rebuildCache(): void {
    const allPaths = this.computeAllPaths();

    // Build cached path data
    this.cachedPaths = allPaths.map((path, index) => this.buildCachedPathData(path, index));

    // Set initial center from field if no paths (for upload scenario)
    if (allPaths.length === 0 && this.fieldCenter) {
      this.center = this.fieldCenter;
      this.zoom = 14;
    }
  }

  /**
   * Compute all paths (combines single and multiple) - private, called only on rebuild
   */
  private computeAllPaths(): FlightPathData[] {
    const paths: FlightPathData[] = [];
    if (this.flightPath) {
      paths.push(this.flightPath);
    }
    return [...paths, ...this.flightPaths];
  }

  /**
   * Build cached data for a single path
   */
  private buildCachedPathData(path: FlightPathData, index: number): CachedPathData {
    const color = this.pathColors[index % this.pathColors.length];

    const startPos = path.coordinates.length > 0
      ? { lat: path.coordinates[0].lat, lng: path.coordinates[0].lng }
      : null;

    const endPos = path.coordinates.length > 0
      ? { lat: path.coordinates[path.coordinates.length - 1].lat, lng: path.coordinates[path.coordinates.length - 1].lng }
      : null;

    // Use numeric value 0 for CIRCLE to avoid depending on google.maps being loaded
    // google.maps.SymbolPath.CIRCLE = 0
    const CIRCLE_PATH = 0;

    return {
      path,
      polylinePath: path.coordinates.map(c => ({ lat: c.lat, lng: c.lng })),
      polylineOptions: {
        ...this.basePolylineOptions,
        strokeColor: color
      },
      startPosition: startPos,
      endPosition: endPos,
      startMarkerOptions: {
        icon: {
          path: CIRCLE_PATH,
          scale: 8,
          fillColor: '#22C55E',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2
        },
        title: 'Start: ' + path.name
      },
      endMarkerOptions: {
        icon: {
          path: CIRCLE_PATH,
          scale: 8,
          fillColor: '#EF4444',
          fillOpacity: 1,
          strokeColor: '#FFFFFF',
          strokeWeight: 2
        },
        title: 'End: ' + path.name
      }
    };
  }

  /**
   * Show info window for a path
   */
  showPathInfo(path: FlightPathData, marker: MapMarker): void {
    this.selectedPath = path;
    this.infoWindow?.open(marker);
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  /**
   * Fit map bounds to show all paths
   */
  fitBounds(): void {
    if (this.cachedPaths.length === 0 || !this.googleMap?.googleMap) return;

    const allCoords = this.cachedPaths.flatMap(cp => cp.path.coordinates);
    if (allCoords.length === 0) return;

    const map = this.googleMap.googleMap;

    const bounds = new google.maps.LatLngBounds();
    allCoords.forEach(coord => {
      bounds.extend({ lat: coord.lat, lng: coord.lng });
    });

    // Fit bounds with padding
    map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });

    // Ensure we don't zoom in too close - max zoom 16 for better overview
    google.maps.event.addListenerOnce(map, 'idle', () => {
      const currentZoom = map.getZoom();
      if (currentZoom && currentZoom > 16) {
        map.setZoom(16);
      }
    });
  }

  /**
   * TrackBy function for ngFor performance
   */
  trackByPathId(index: number, item: CachedPathData): string {
    return item.path.id;
  }

  // ============================================
  // FILE UPLOAD HANDLING
  // ============================================

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFile(input.files[0]);
    }
  }

  private processFile(file: File): void {
    // Validate file type
    const validTypes = ['.kml', '.kmz', '.gpx'];
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!validTypes.includes(extension)) {
      this.uploadError = 'Please upload a KML, KMZ, or GPX file.';
      return;
    }

    this.isProcessing = true;
    this.uploadError = null;

    this.flightMapService.parseKmlFile(file).subscribe({
      next: (flightPath) => {
        this.isProcessing = false;
        this.fileUploaded.emit(flightPath);
      },
      error: (err) => {
        this.isProcessing = false;
        this.uploadError = 'Failed to process file. Please try again.';
        console.error('File processing error:', err);
      }
    });
  }

  cancelUpload(): void {
    this.uploadCancelled.emit();
  }
}
